import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  PhoneCall,
  Unlock,
  XCircle,
} from "lucide-react";
import {
  CasePhoneNumbersTable,
  formatPhoneListInput,
  parsePhoneListInput,
  type CasePhoneNumbersView,
} from "@/components/case-phone-numbers-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  useRetryExecute,
  useRetryPreflight,
  useRetryReleaseBatchHold,
} from "@/hooks/use-retry";
import { failClassLabel } from "@/lib/intake-format";
import { cn } from "@/utils";
import type {
  RetryCaseOverrides,
  RetryNeedsInputRow,
  RetryPhoneFields,
  RetryReadyRow,
  RetryReleaseBatchHoldResponse,
  RetryRequestBody,
} from "@/types/intake";

type Step = "input" | "validate" | "executed";

function parseCaseIds(raw: string): string[] {
  return Array.from(
    new Set(
      raw
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  );
}

export default function RetryConsolePage() {
  const [searchParams] = useSearchParams();
  const initialIdsParam = searchParams.get("case_ids") || "";
  const fromDate = searchParams.get("from");

  const [rawIds, setRawIds] = useState<string>(
    initialIdsParam.replace(/,/g, "\n"),
  );
  const [overrides, setOverrides] = useState<Record<string, RetryCaseOverrides>>(
    {},
  );
  const [availableDrafts, setAvailableDrafts] = useState<Record<string, string>>(
    {},
  );
  const [availableEdited, setAvailableEdited] = useState<Set<string>>(
    () => new Set(),
  );
  const [retryProductName, setRetryProductName] = useState("");
  const [step, setStep] = useState<Step>("input");

  const {
    result: preflight,
    loading: preflightLoading,
    error: preflightError,
    run: runPreflight,
    reset: resetPreflight,
  } = useRetryPreflight();
  const {
    result: executeResult,
    loading: executing,
    error: executeError,
    run: runExecute,
  } = useRetryExecute();
  const {
    result: releaseResult,
    loading: releasing,
    error: releaseError,
    run: runRelease,
    reset: resetRelease,
  } = useRetryReleaseBatchHold();

  const caseIds = useMemo(() => parseCaseIds(rawIds), [rawIds]);

  const overridesPayload = useMemo(() => {
    const out: NonNullable<RetryRequestBody["overrides"]> = {};
    for (const [caseId, o] of Object.entries(overrides)) {
      const dial = o.payer_phone_number?.trim();
      const includeAvail = availableEdited.has(caseId);
      if (!dial && !includeAvail) continue;
      out[caseId] = {
        ...(dial ? { payer_phone_number: dial } : {}),
        ...(includeAvail && o.available_phone_numbers !== undefined
          ? { available_phone_numbers: o.available_phone_numbers }
          : {}),
      };
    }
    return out;
  }, [overrides, availableEdited]);

  const retryRequestOptions = useMemo(() => {
    const t = retryProductName.trim();
    return t ? { retry_product_name: t } : {};
  }, [retryProductName]);

  const buildRetryBody = useCallback(
    (ids: string[]): RetryRequestBody => ({
      case_ids: ids,
      overrides: overridesPayload,
      ...retryRequestOptions,
    }),
    [overridesPayload, retryRequestOptions],
  );

  // Auto-run preflight when arriving with ?case_ids= prefill.
  useEffect(() => {
    if (initialIdsParam && step === "input") {
      const ids = parseCaseIds(initialIdsParam);
      if (ids.length > 0) {
        void runPreflight({ case_ids: ids, overrides: {} }).then(() =>
          setStep("validate"),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleValidate = useCallback(async () => {
    if (caseIds.length === 0) return;
    await runPreflight(buildRetryBody(caseIds));
    setStep("validate");
  }, [caseIds, buildRetryBody, runPreflight]);

  const handleRevalidate = useCallback(async () => {
    const idsToRevalidate = preflight
      ? [
          ...preflight.ready.map((r) => r.case_id),
          ...preflight.needs_input.map((r) => r.case_id),
          ...preflight.blocked.map((r) => r.case_id),
        ]
      : caseIds;
    await runPreflight(buildRetryBody(idsToRevalidate));
  }, [preflight, caseIds, buildRetryBody, runPreflight]);

  const handleExecute = useCallback(async () => {
    if (!preflight) return;
    // Include all rows the user fixed via overrides (which may have moved from
    // needs_input → ready) plus the ready set itself.
    const ready = new Set(preflight.ready.map((r) => r.case_id));
    const fixed = preflight.needs_input
      .map((r) => r.case_id)
      .filter(
        (id) =>
          Boolean((overrides[id]?.payer_phone_number || "").trim()) ||
          availableEdited.has(id),
      );
    const ids = Array.from(new Set([...ready, ...fixed]));
    if (ids.length === 0) return;
    await runExecute(buildRetryBody(ids));
    setStep("executed");
  }, [preflight, overrides, availableEdited, buildRetryBody, runExecute]);

  const handleStartOver = useCallback(() => {
    resetPreflight();
    resetRelease();
    setOverrides({});
    setAvailableDrafts({});
    setAvailableEdited(new Set());
    setRetryProductName("");
    setRawIds("");
    setStep("input");
  }, [resetPreflight, resetRelease]);

  const handleReleaseBatchHolds = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0) return;
      try {
        await runRelease(ids);
      } catch {
        return;
      }
      // Re-validate so released rows flip from blocked → ready (or
      // surface as not_duplicatable if VAPI still considers the call active).
      await handleRevalidate();
    },
    [runRelease, handleRevalidate],
  );

  const updateDialOverride = useCallback((caseId: string, value: string) => {
    setOverrides((prev) => ({
      ...prev,
      [caseId]: { ...prev[caseId], payer_phone_number: value },
    }));
  }, []);

  const updateAvailableDraft = useCallback((caseId: string, text: string) => {
    setAvailableDrafts((prev) => ({ ...prev, [caseId]: text }));
    setAvailableEdited((prev) => new Set(prev).add(caseId));
    setOverrides((prev) => ({
      ...prev,
      [caseId]: {
        ...prev[caseId],
        available_phone_numbers: parsePhoneListInput(text),
      },
    }));
  }, []);

  const ready = preflight?.ready ?? [];
  const needsInput = preflight?.needs_input ?? [];
  const blocked = preflight?.blocked ?? [];

  const fixableNow = needsInput.filter((r) =>
    Boolean((overrides[r.case_id]?.payer_phone_number || "").trim()),
  );
  const totalToExecute = ready.length + fixableNow.length;

  const releasableBlocked = useMemo(
    () => blocked.filter((b) => b.reason === "in_active_batch"),
    [blocked],
  );

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Retry Console</h1>
          <p className="text-sm text-muted-foreground">
            Validate retry eligibility and dispatch retries without leaving the
            dashboard.
            {fromDate ? (
              <>
                {" · From "}
                <Link
                  to={`/intake?date=${encodeURIComponent(fromDate)}`}
                  className="underline-offset-2 hover:underline"
                >
                  {fromDate} intake
                </Link>
              </>
            ) : null}
          </p>
        </div>
        {fromDate ? (
          <Button asChild variant="ghost" size="sm">
            <Link to={`/intake?date=${encodeURIComponent(fromDate)}`}>
              <ArrowLeft className="mr-1 h-4 w-4" />
              Back to intake
            </Link>
          </Button>
        ) : null}
      </div>

      {/* Step indicator */}
      <ol className="flex items-center gap-2 text-xs">
        <StepPill index={1} label="Source" active={step === "input"} done={step !== "input"} />
        <StepPill
          index={2}
          label="Validate"
          active={step === "validate"}
          done={step === "executed"}
        />
        <StepPill index={3} label="Retry" active={step === "executed"} />
      </ol>

      {step === "input" ? (
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="case-ids-input">
              Case IDs (comma- or newline-separated)
            </label>
            <textarea
              id="case-ids-input"
              value={rawIds}
              onChange={(e) => setRawIds(e.target.value)}
              rows={8}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
              placeholder="018ac3b8-ec5f-4380-b502-71269a1bb108&#10;05b57896-6a6a-40bd-9fb0-baa84c6243f1"
            />
            <p className="text-xs text-muted-foreground">
              {caseIds.length} unique case ID
              {caseIds.length === 1 ? "" : "s"} parsed.
            </p>
          </div>
          {preflightError ? (
            <p className="text-sm text-destructive">{preflightError}</p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button
              onClick={handleValidate}
              disabled={caseIds.length === 0 || preflightLoading}
            >
              {preflightLoading ? "Validating…" : "Validate"}
            </Button>
          </div>
        </div>
      ) : null}

      {step === "validate" ? (
        <div className="space-y-6">
          {preflightLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Validating {caseIds.length} cases…
            </div>
          ) : null}
          {preflightError ? (
            <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              {preflightError}
            </div>
          ) : null}
          {preflight ? (
            <>
              <SummaryRow
                ready={ready.length}
                needsInput={needsInput.length}
                blocked={blocked.length}
                total={preflight.summary.total}
              />

              <div className="space-y-2 rounded-md border bg-muted/20 p-3">
                <label
                  className="text-sm font-medium"
                  htmlFor="retry-product-name-override"
                >
                  Override product name (all cases)
                </label>
                <Input
                  id="retry-product-name-override"
                  value={retryProductName}
                  onChange={(e) => setRetryProductName(e.target.value)}
                  placeholder="Leave blank to keep each case’s stored product"
                  className="max-w-xl"
                />
                <p className="text-xs text-muted-foreground">
                  When set, this value replaces{" "}
                  <code className="rounded bg-muted px-1 py-0.5 text-[11px]">
                    Product Name #1
                  </code>{" "}
                  on every case in this retry, and is written to Firestore when you
                  run Retry, and will be sent to assistant. Use Re-validate after editing so preflight reflects the new
                  name.
                </p>
              </div>

              <Section
                title={`Ready (${ready.length})`}
                tone="success"
                description="These cases will retry as-is."
              >
                <ReadyTable
                  rows={ready}
                  overrides={overrides}
                  availableDrafts={availableDrafts}
                  onDialChange={updateDialOverride}
                  onAvailableChange={updateAvailableDraft}
                />
              </Section>

              <Section
                title={`Needs input (${needsInput.length})`}
                tone="warning"
                description="Click a number to set the dial target, or type one below. Edit available numbers to replace BIN/PCN lookups (persisted on execute; clears tried rotation). Re-validate after changes."
              >
                <NeedsInputTable
                  rows={needsInput}
                  overrides={overrides}
                  availableDrafts={availableDrafts}
                  onDialChange={updateDialOverride}
                  onAvailableChange={updateAvailableDraft}
                />
              </Section>

              <Section
                title={`Blocked (${blocked.length})`}
                tone="failure"
                description="Cannot be retried. Common reasons: already successful, currently in an active retry batch, or not eligible per duplicate-call check."
              >
                {releasableBlocked.length > 0 ? (
                  <div className="flex flex-wrap items-center gap-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs dark:border-amber-900 dark:bg-amber-950/30">
                    <div className="flex-1">
                      <p className="font-medium text-amber-900 dark:text-amber-100">
                        {releasableBlocked.length} case
                        {releasableBlocked.length === 1 ? " is" : "s are"} held
                        by an active retry batch.
                      </p>
                      <p className="mt-0.5 text-amber-900/80 dark:text-amber-100/80">
                        Releasing only removes the case from
                        {" "}
                        <code className="rounded bg-amber-100 px-1 py-0.5 dark:bg-amber-900/40">
                          batchRetryState
                        </code>
                        . It does <strong>not</strong> cancel any VAPI call
                        already scheduled — if VAPI still has the call active,
                        re-validate will block the case again with
                        {" "}
                        <em>not_duplicatable</em>, preventing a double-dial.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1"
                      disabled={releasing || preflightLoading}
                      onClick={() =>
                        handleReleaseBatchHolds(
                          releasableBlocked.map((r) => r.case_id),
                        )
                      }
                    >
                      {releasing ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          Releasing…
                        </>
                      ) : (
                        <>
                          <Unlock className="h-3.5 w-3.5" />
                          Release batch hold ({releasableBlocked.length})
                        </>
                      )}
                    </Button>
                  </div>
                ) : null}
                {releaseError ? (
                  <p className="mt-2 text-xs text-destructive">
                    {releaseError}
                  </p>
                ) : null}
                {releaseResult ? (
                  <ReleaseResultSummary result={releaseResult} />
                ) : null}
                <BlockedTable rows={blocked} />
              </Section>

              <div className="sticky bottom-4 z-10 flex flex-wrap items-center justify-between gap-3 rounded-md border bg-background p-3 shadow-sm">
                <div className="text-sm">
                  <span className="font-medium">{totalToExecute}</span>{" "}
                  case{totalToExecute === 1 ? "" : "s"} ready to retry
                  {fixableNow.length > 0 ? (
                    <>
                      {" "}
                      <span className="text-muted-foreground">
                        (incl. {fixableNow.length} with new phone)
                      </span>
                    </>
                  ) : null}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleRevalidate}
                    disabled={preflightLoading}
                  >
                    Re-validate
                  </Button>
                  <Button variant="ghost" onClick={handleStartOver}>
                    Start over
                  </Button>
                  <Button
                    onClick={handleExecute}
                    disabled={totalToExecute === 0 || executing}
                    className="gap-1"
                  >
                    {executing ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Retrying…
                      </>
                    ) : (
                      <>
                        <PhoneCall className="h-4 w-4" />
                        Retry {totalToExecute} case{totalToExecute === 1 ? "" : "s"}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </>
          ) : null}
        </div>
      ) : null}

      {step === "executed" ? (
        <div className="space-y-4">
          {executing ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Dispatching retries…
            </div>
          ) : null}
          {executeError ? (
            <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              {executeError}
            </div>
          ) : null}
          {executeResult ? (
            <div className="rounded-md border bg-muted/20 p-4">
              <div className="flex items-center gap-2 text-sm font-medium">
                {executeResult.success ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    Retries dispatched
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4 text-destructive" />
                    Retry dispatch did not complete cleanly
                  </>
                )}
              </div>
              <dl className="mt-3 grid grid-cols-2 gap-y-1 text-xs sm:grid-cols-4">
                <ResultStat label="Total submitted" value={executeResult.cases_total} />
                <ResultStat label="Processed" value={executeResult.cases_processed} />
                <ResultStat label="Created" value={executeResult.cases_created} />
                <ResultStat label="Skipped" value={executeResult.cases_skipped} />
                <ResultStat label="Invalid" value={executeResult.cases_invalid} />
                <ResultStat label="Errors" value={executeResult.cases_errors} />
                <ResultStat
                  label="Fetch errors"
                  value={executeResult.retry_fetch_errors}
                />
                <ResultStat
                  label="Deferred"
                  value={executeResult.deferred_telephony ? "Yes" : "No"}
                />
              </dl>
              {executeResult.batch_id ? (
                <p className="mt-3 text-xs text-muted-foreground">
                  Last batch id:{" "}
                  <code className="rounded bg-muted px-1 py-0.5">
                    {executeResult.batch_id}
                  </code>
                </p>
              ) : null}
              {executeResult.message ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  {executeResult.message}
                </p>
              ) : null}
              {executeResult.errors && executeResult.errors.length > 0 ? (
                <div className="mt-4">
                  <p className="text-xs font-medium">
                    Row-level errors ({executeResult.errors.length}):
                  </p>
                  <ScrollArea className="mt-1 max-h-48 w-full rounded-md border">
                    <ul className="px-3 py-2 text-xs">
                      {executeResult.errors.map((e, i) => (
                        <li key={i} className="border-b py-1 last:border-b-0">
                          <span className="font-mono text-[11px]">
                            {e.case_id || "(no case_id)"}
                          </span>
                          : {e.error}
                        </li>
                      ))}
                    </ul>
                  </ScrollArea>
                </div>
              ) : null}
            </div>
          ) : null}
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleStartOver}>
              Start another retry
            </Button>
            {fromDate ? (
              <Button asChild variant="ghost">
                <Link to={`/intake?date=${encodeURIComponent(fromDate)}`}>
                  <ArrowLeft className="mr-1 h-4 w-4" />
                  Back to intake
                </Link>
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function StepPill({
  index,
  label,
  active,
  done,
}: {
  index: number;
  label: string;
  active?: boolean;
  done?: boolean;
}) {
  return (
    <li
      className={cn(
        "flex items-center gap-2 rounded-md border px-3 py-1.5",
        active
          ? "border-primary bg-primary text-primary-foreground"
          : done
            ? "border-green-200 bg-green-50 text-green-900 dark:border-green-900 dark:bg-green-950/40 dark:text-green-100"
            : "border-border bg-background text-muted-foreground",
      )}
    >
      <span className="font-mono">{index}</span>
      <span className="font-medium">{label}</span>
    </li>
  );
}

function SummaryRow({
  ready,
  needsInput,
  blocked,
  total,
}: {
  ready: number;
  needsInput: number;
  blocked: number;
  total: number;
}) {
  return (
    <div className="flex flex-wrap gap-2 text-xs">
      <span className="rounded-md border border-green-200 bg-green-50 px-3 py-1.5 dark:border-green-900 dark:bg-green-950/40">
        Ready · {ready}
      </span>
      <span className="rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 dark:border-amber-900 dark:bg-amber-950/40">
        Needs input · {needsInput}
      </span>
      <span className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-1.5">
        Blocked · {blocked}
      </span>
      <span className="rounded-md border bg-muted/30 px-3 py-1.5">
        Total · {total}
      </span>
    </div>
  );
}

function Section({
  title,
  tone,
  description,
  children,
}: {
  title: string;
  tone: "success" | "warning" | "failure";
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2">
      <header>
        <h2
          className={cn(
            "text-sm font-semibold",
            tone === "success" && "text-green-700 dark:text-green-300",
            tone === "warning" && "text-amber-800 dark:text-amber-200",
            tone === "failure" && "text-destructive",
          )}
        >
          {title}
        </h2>
        {description ? (
          <p className="text-xs text-muted-foreground">{description}</p>
        ) : null}
      </header>
      {children}
    </section>
  );
}

function retryPhoneView(
  row: RetryPhoneFields,
  caseOverrides: RetryCaseOverrides | undefined,
): CasePhoneNumbersView {
  const dialOverride = caseOverrides?.payer_phone_number?.trim();
  const available =
    caseOverrides?.available_phone_numbers ?? row.available_phone_numbers;
  const firstAvailable = available?.[0]?.trim();
  return {
    payer_phone_number:
      dialOverride || firstAvailable || row.payer_phone_number,
    available_phone_numbers: available ?? [],
    tried_phone_numbers: row.tried_phone_numbers ?? [],
    all_numbers_exhausted: row.all_numbers_exhausted,
    alternate_contact_number: row.alternate_contact_number,
  };
}

function availableDraftForRow(
  caseId: string,
  row: RetryPhoneFields,
  availableDrafts: Record<string, string>,
  caseOverrides: RetryCaseOverrides | undefined,
): string {
  if (caseId in availableDrafts) return availableDrafts[caseId];
  if (caseOverrides?.available_phone_numbers) {
    return formatPhoneListInput(caseOverrides.available_phone_numbers);
  }
  return formatPhoneListInput(row.available_phone_numbers ?? []);
}

function RetryPhoneCell({
  row,
  caseOverrides,
  availableDraft,
  onDialChange,
  onAvailableChange,
  showDialInput,
}: {
  row: RetryPhoneFields & { case_id: string };
  caseOverrides?: RetryCaseOverrides;
  availableDraft: string;
  onDialChange: (caseId: string, value: string) => void;
  onAvailableChange: (caseId: string, text: string) => void;
  showDialInput?: boolean;
}) {
  const view = retryPhoneView(row, caseOverrides);
  const dialValue = caseOverrides?.payer_phone_number ?? row.payer_phone_number ?? "";

  return (
    <div className="min-w-[200px] space-y-2">
      <CasePhoneNumbersTable
        view={view}
        selectedPhone={dialValue || undefined}
        onSelectPhone={(phone) => onDialChange(row.case_id, phone)}
      />
      {showDialInput ? (
        <Input
          value={dialValue}
          placeholder="+1XXXXXXXXXX"
          className="h-8 font-mono text-[11px]"
          onChange={(e) => onDialChange(row.case_id, e.target.value)}
        />
      ) : null}
      <div className="space-y-1">
        <label
          className="text-[10px] font-medium text-muted-foreground"
          htmlFor={`avail-${row.case_id}`}
        >
          Override available numbers
        </label>
        <textarea
          id={`avail-${row.case_id}`}
          value={availableDraft}
          rows={3}
          placeholder="One E.164 number per line"
          className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex min-h-[60px] w-full rounded-md border px-2 py-1.5 font-mono text-[11px] focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
          onChange={(e) => onAvailableChange(row.case_id, e.target.value)}
        />
      </div>
    </div>
  );
}

function ReadyTable({
  rows,
  overrides,
  availableDrafts,
  onDialChange,
  onAvailableChange,
}: {
  rows: RetryReadyRow[];
  overrides: Record<string, RetryCaseOverrides>;
  availableDrafts: Record<string, string>;
  onDialChange: (caseId: string, value: string) => void;
  onAvailableChange: (caseId: string, text: string) => void;
}) {
  if (rows.length === 0) return <EmptyHint>No cases ready.</EmptyHint>;
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full min-w-[1100px] border-collapse">
        <thead className="bg-muted/60 text-left text-xs font-medium text-muted-foreground">
          <tr>
            <th className="px-2 py-2">Case</th>
            <th className="px-2 py-2">Patient</th>
            <th className="px-2 py-2">Product</th>
            <th className="px-2 py-2">Payer</th>
            <th className="px-2 py-2">Phone numbers</th>
            <th className="px-2 py-2">Bucket</th>
            <th className="px-2 py-2">Attempts</th>
          </tr>
        </thead>
        <tbody className="text-sm">
          {rows.map((r) => (
            <tr key={r.case_id} className="border-b text-xs align-top">
              <td className="px-2 py-2 font-mono text-[11px]" title={r.case_id}>
                {r.case_id.slice(0, 12)}…
              </td>
              <td className="px-2 py-2">{r.name}</td>
              <td className="px-2 py-2 max-w-[200px] truncate" title={r.product_name}>
                {r.product_name || "—"}
              </td>
              <td className="px-2 py-2">{r.payer_name || "—"}</td>
              <td className="px-2 py-2">
                <RetryPhoneCell
                  row={r}
                  caseOverrides={overrides[r.case_id]}
                  availableDraft={availableDraftForRow(
                    r.case_id,
                    r,
                    availableDrafts,
                    overrides[r.case_id],
                  )}
                  onDialChange={onDialChange}
                  onAvailableChange={onAvailableChange}
                />
              </td>
              <td className="px-2 py-2 capitalize">
                {failClassLabel(r.fail_class).toLowerCase()}
              </td>
              <td className="px-2 py-2 tabular-nums">{r.attempts}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NeedsInputTable({
  rows,
  overrides,
  availableDrafts,
  onDialChange,
  onAvailableChange,
}: {
  rows: RetryNeedsInputRow[];
  overrides: Record<string, RetryCaseOverrides>;
  availableDrafts: Record<string, string>;
  onDialChange: (caseId: string, value: string) => void;
  onAvailableChange: (caseId: string, text: string) => void;
}) {
  if (rows.length === 0) return <EmptyHint>No cases need input.</EmptyHint>;
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full min-w-[1100px] border-collapse">
        <thead className="bg-muted/60 text-left text-xs font-medium text-muted-foreground">
          <tr>
            <th className="px-2 py-2">Case</th>
            <th className="px-2 py-2">Patient</th>
            <th className="px-2 py-2">Product</th>
            <th className="px-2 py-2">Payer</th>
            <th className="px-2 py-2">Phone numbers</th>
          </tr>
        </thead>
        <tbody className="text-sm">
          {rows.map((r) => {
            const suggested =
              r.suggested_phone || r.alternate_contact_number || "";
            return (
              <tr key={r.case_id} className="border-b text-xs align-top">
                <td
                  className="px-2 py-2 font-mono text-[11px]"
                  title={r.case_id}
                >
                  {r.case_id.slice(0, 12)}…
                </td>
                <td className="px-2 py-2">{r.name}</td>
                <td className="px-2 py-2 max-w-[200px] truncate" title={r.product_name}>
                  {r.product_name || "—"}
                </td>
                <td className="px-2 py-2">{r.payer_name || "—"}</td>
                <td className="px-2 py-2">
                  {suggested ? (
                    <p className="mb-2 text-[10px] text-muted-foreground">
                      Suggested:{" "}
                      <button
                        type="button"
                        className="font-mono underline-offset-2 hover:underline"
                        onClick={() => onDialChange(r.case_id, suggested)}
                      >
                        {suggested}
                      </button>
                    </p>
                  ) : null}
                  <RetryPhoneCell
                    row={r}
                    caseOverrides={overrides[r.case_id]}
                    availableDraft={availableDraftForRow(
                      r.case_id,
                      r,
                      availableDrafts,
                      overrides[r.case_id],
                    )}
                    showDialInput
                    onDialChange={onDialChange}
                    onAvailableChange={onAvailableChange}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function BlockedTable({
  rows,
}: {
  rows: {
    case_id: string;
    reason: string;
    detail?: string;
    batch_id?: string;
    outbound_scheduled_for?: string | null;
  }[];
}) {
  if (rows.length === 0) return <EmptyHint>No blocked cases.</EmptyHint>;
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full min-w-[640px] border-collapse">
        <thead className="bg-muted/60 text-left text-xs font-medium text-muted-foreground">
          <tr>
            <th className="px-2 py-2">Case</th>
            <th className="px-2 py-2">Reason</th>
            <th className="px-2 py-2">Detail</th>
          </tr>
        </thead>
        <tbody className="text-sm">
          {rows.map((r, i) => (
            <tr key={`${r.case_id}-${i}`} className="border-b text-xs">
              <td className="px-2 py-2 font-mono text-[11px]" title={r.case_id}>
                {r.case_id ? `${r.case_id.slice(0, 12)}…` : "—"}
              </td>
              <td className="px-2 py-2 capitalize">{r.reason.replace(/_/g, " ")}</td>
              <td className="px-2 py-2 text-muted-foreground">
                {r.detail || (r.batch_id ? `batch ${r.batch_id}` : "—")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReleaseResultSummary({
  result,
}: {
  result: RetryReleaseBatchHoldResponse;
}) {
  const released = result.results.filter((r) => r.ok && r.released).length;
  const noop = result.results.filter(
    (r) => r.ok && !r.released && r.detail === "not_in_active_batch",
  ).length;
  const stuck = result.results.filter(
    (r) => r.ok && r.still_in_active_batch,
  ).length;
  const failed = result.results.filter((r) => !r.ok).length;
  return (
    <div className="mt-2 rounded-md border bg-muted/20 p-2 text-xs">
      <div className="flex flex-wrap gap-3">
        <span>
          Released: <span className="font-semibold tabular-nums">{released}</span>
        </span>
        <span className="text-muted-foreground">
          Already free:{" "}
          <span className="font-semibold tabular-nums">{noop}</span>
        </span>
        {stuck > 0 ? (
          <span className="text-amber-700 dark:text-amber-300">
            Still held:{" "}
            <span className="font-semibold tabular-nums">{stuck}</span>
          </span>
        ) : null}
        {failed > 0 ? (
          <span className="text-destructive">
            Errored:{" "}
            <span className="font-semibold tabular-nums">{failed}</span>
          </span>
        ) : null}
      </div>
      {failed > 0 ? (
        <ul className="mt-1 space-y-0.5">
          {result.results
            .filter((r) => !r.ok)
            .slice(0, 5)
            .map((r) => (
              <li key={r.case_id} className="text-destructive">
                <span className="font-mono text-[11px]">
                  {r.case_id.slice(0, 12)}…
                </span>
                : {r.error || "unknown error"}
              </li>
            ))}
        </ul>
      ) : null}
    </div>
  );
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-md border bg-muted/30 px-3 py-3 text-xs text-muted-foreground">
      {children}
    </p>
  );
}

function ResultStat({
  label,
  value,
}: {
  label: string;
  value: number | string | undefined;
}) {
  return (
    <div className="flex flex-col">
      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="text-sm font-semibold tabular-nums">
        {value === undefined || value === null ? "—" : String(value)}
      </dd>
    </div>
  );
}
